import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { Label } from '@components/ui/label'
import { Textarea } from '@components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select'
import type { PathologicalEntry, HistoryItemType, DatePrecision } from '@/types'

// ── Labels ───────────────────────────────────────────────────────────────────

const ITEM_TYPE_LABELS: Record<HistoryItemType, string> = {
  chronic_degenerative: 'Enfermedad crónico-degenerativa',
  surgery: 'Cirugía',
  trauma: 'Traumatismo',
  transfusion: 'Transfusión',
  allergy: 'Alergia',
  hospitalization: 'Hospitalización previa',
  vaccination: 'Vacunación',
  current_medication: 'Medicamento actual',
  other: 'Otro',
}

const CONDITION_LABEL: Record<HistoryItemType, string> = {
  chronic_degenerative: 'Nombre de la enfermedad',
  surgery: 'Procedimiento realizado',
  trauma: 'Descripción del traumatismo',
  transfusion: 'Descripción general',
  allergy: 'Agente o nombre de la alergia',
  hospitalization: 'Motivo principal de hospitalización',
  vaccination: 'Nombre de la vacuna',
  current_medication: 'Nombre del medicamento',
  other: 'Nombre o descripción',
}

const CONDITION_PLACEHOLDER: Record<HistoryItemType, string> = {
  chronic_degenerative: 'ej. Diabetes tipo 2',
  surgery: 'ej. Apendicectomía',
  trauma: 'ej. Fractura de tibia derecha',
  transfusion: 'ej. Transfusión de concentrado eritrocitario',
  allergy: 'ej. Alergia a penicilina',
  hospitalization: 'ej. Neumonía grave',
  vaccination: 'ej. Influenza estacional',
  current_medication: 'ej. Metformina',
  other: 'ej. Descripción del antecedente',
}

const SEVERITY_LABELS: Record<string, string> = {
  mild: 'Leve',
  moderate: 'Moderada',
  severe: 'Severa',
}

// ── Default form state ────────────────────────────────────────────────────────

function buildEmpty(): FormState {
  return {
    item_type: '' as HistoryItemType,
    condition: '',
    date_precision: 'exact',
    diagnosis_date: '',
    date_approximate: '',
    date_year: '',
    status: 'active',
    physician_notes: '',
    related_item_idx: '',
    // Surgery
    surgery_procedure: '',
    surgery_hospital: '',
    surgery_physician: '',
    // Allergy
    allergy_causative_agent: '',
    allergy_reaction_type: '',
    allergy_severity: 'moderate',
    // Chronic
    chronic_treatment: '',
    chronic_specialist: '',
    // Transfusion
    transfusion_reason: '',
    transfusion_blood_product: '',
    transfusion_reaction: '',
    // Hospitalization
    hospitalization_reason: '',
    hospitalization_duration: '',
    hospitalization_hospital: '',
    // Vaccination
    vaccination_name: '',
    vaccination_dose: '',
    // Medication
    medication_name: '',
    medication_dosage: '',
    medication_frequency: '',
    medication_start_date: '',
  }
}

interface FormState {
  item_type: HistoryItemType | ''
  condition: string
  date_precision: DatePrecision
  diagnosis_date: string
  date_approximate: string
  date_year: string
  status: PathologicalEntry['status']
  physician_notes: string
  related_item_idx: string
  surgery_procedure: string
  surgery_hospital: string
  surgery_physician: string
  allergy_causative_agent: string
  allergy_reaction_type: string
  allergy_severity: 'mild' | 'moderate' | 'severe'
  chronic_treatment: string
  chronic_specialist: string
  transfusion_reason: string
  transfusion_blood_product: string
  transfusion_reaction: string
  hospitalization_reason: string
  hospitalization_duration: string
  hospitalization_hospital: string
  vaccination_name: string
  vaccination_dose: string
  medication_name: string
  medication_dosage: string
  medication_frequency: string
  medication_start_date: string
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  existingItems: PathologicalEntry[]
  capturedByName?: string
  isSaving?: boolean
  onSave: (entry: PathologicalEntry) => void
}

export function AddHistoryItemDialog({
  open,
  onOpenChange,
  existingItems,
  capturedByName,
  isSaving,
  onSave,
}: Props) {
  const [form, setForm] = useState<FormState>(buildEmpty)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  const validate = (): boolean => {
    const e: Record<string, string> = {}
    if (!form.item_type) e.item_type = 'Seleccione el tipo de antecedente'
    if (!form.condition.trim()) e.condition = 'Este campo es requerido'
    if (form.date_precision === 'exact' && !form.diagnosis_date) {
      e.diagnosis_date = 'Ingrese la fecha exacta o cambie la precisión'
    }
    if (form.date_precision === 'year_only' && !form.date_year) {
      e.date_year = 'Ingrese el año'
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSave = () => {
    if (!validate()) return

    const entry: PathologicalEntry = {
      condition: form.condition.trim(),
      status: form.status,
      item_type: form.item_type as HistoryItemType,
      date_precision: form.date_precision,
      captured_at: new Date().toISOString(),
      captured_by_name: capturedByName,
    }

    // Resolve date fields
    if (form.date_precision === 'exact' && form.diagnosis_date) {
      entry.diagnosis_date = form.diagnosis_date
    } else if (form.date_precision === 'approximate' && form.date_approximate.trim()) {
      entry.date_approximate = form.date_approximate.trim()
    } else if (form.date_precision === 'year_only' && form.date_year) {
      entry.date_year = Number(form.date_year)
      // Approximate ISO date so timeline can sort by year
      entry.diagnosis_date = `${form.date_year}-01-01`
    }

    if (form.physician_notes.trim()) entry.physician_notes = form.physician_notes.trim()
    if (form.related_item_idx !== '') entry.related_item_idx = Number(form.related_item_idx)

    // Type-specific fields
    if (form.item_type === 'surgery') {
      if (form.surgery_procedure.trim()) entry.surgery_procedure = form.surgery_procedure.trim()
      if (form.surgery_hospital.trim()) entry.surgery_hospital = form.surgery_hospital.trim()
      if (form.surgery_physician.trim()) entry.surgery_physician = form.surgery_physician.trim()
    }
    if (form.item_type === 'allergy') {
      if (form.allergy_causative_agent.trim()) entry.allergy_causative_agent = form.allergy_causative_agent.trim()
      if (form.allergy_reaction_type.trim()) entry.allergy_reaction_type = form.allergy_reaction_type.trim()
      entry.allergy_severity = form.allergy_severity
    }
    if (form.item_type === 'chronic_degenerative') {
      if (form.chronic_treatment.trim()) entry.chronic_treatment = form.chronic_treatment.trim()
      if (form.chronic_specialist.trim()) entry.chronic_specialist = form.chronic_specialist.trim()
    }
    if (form.item_type === 'transfusion') {
      if (form.transfusion_reason.trim()) entry.transfusion_reason = form.transfusion_reason.trim()
      if (form.transfusion_blood_product.trim()) entry.transfusion_blood_product = form.transfusion_blood_product.trim()
      if (form.transfusion_reaction.trim()) entry.transfusion_reaction = form.transfusion_reaction.trim()
    }
    if (form.item_type === 'hospitalization') {
      if (form.hospitalization_reason.trim()) entry.hospitalization_reason = form.hospitalization_reason.trim()
      if (form.hospitalization_duration.trim()) entry.hospitalization_duration = form.hospitalization_duration.trim()
      if (form.hospitalization_hospital.trim()) entry.hospitalization_hospital = form.hospitalization_hospital.trim()
    }
    if (form.item_type === 'vaccination') {
      if (form.vaccination_name.trim()) entry.vaccination_name = form.vaccination_name.trim()
      if (form.vaccination_dose.trim()) entry.vaccination_dose = form.vaccination_dose.trim()
    }
    if (form.item_type === 'current_medication') {
      if (form.medication_name.trim()) entry.medication_name = form.medication_name.trim()
      if (form.medication_dosage.trim()) entry.medication_dosage = form.medication_dosage.trim()
      if (form.medication_frequency.trim()) entry.medication_frequency = form.medication_frequency.trim()
      if (form.medication_start_date) entry.medication_start_date = form.medication_start_date
    }

    onSave(entry)
    setForm(buildEmpty())
    setErrors({})
  }

  const handleClose = () => {
    setForm(buildEmpty())
    setErrors({})
    onOpenChange(false)
  }

  const typeSet = !!form.item_type

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose() }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Agregar antecedente patológico</DialogTitle>
          <DialogDescription>
            Registre un nuevo antecedente. Los campos mostrados se adaptan al tipo seleccionado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* ── Type selector ─────────────────────────────────────────────── */}
          <Field label="Tipo de antecedente *" error={errors.item_type}>
            <Select value={form.item_type} onValueChange={(v) => set('item_type', v as HistoryItemType)}>
              <SelectTrigger className={errors.item_type ? 'border-destructive' : ''}>
                <SelectValue placeholder="Seleccionar tipo…" />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(ITEM_TYPE_LABELS) as HistoryItemType[]).map((t) => (
                  <SelectItem key={t} value={t}>{ITEM_TYPE_LABELS[t]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {/* ── Name / description ────────────────────────────────────────── */}
          <Field
            label={`${typeSet ? CONDITION_LABEL[form.item_type as HistoryItemType] : 'Nombre o descripción'} *`}
            error={errors.condition}
          >
            <Input
              value={form.condition}
              onChange={(e) => set('condition', e.target.value)}
              placeholder={typeSet ? CONDITION_PLACEHOLDER[form.item_type as HistoryItemType] : 'Descripción del antecedente'}
              className={errors.condition ? 'border-destructive' : ''}
            />
          </Field>

          {/* ── Date section ─────────────────────────────────────────────── */}
          <div className="rounded-lg border border-ml-outline-variant/20 bg-ml-surface-container-lowest/40 p-4 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-ml-on-surface-variant/70">
              Fecha del evento
            </p>
            <div>
              <Label className="text-xs font-medium">Precisión de la fecha</Label>
              <Select value={form.date_precision} onValueChange={(v) => set('date_precision', v as DatePrecision)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="exact">Fecha exacta (día/mes/año)</SelectItem>
                  <SelectItem value="approximate">Fecha aproximada (texto libre)</SelectItem>
                  <SelectItem value="year_only">Solo el año</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.date_precision === 'exact' && (
              <Field label="Fecha" error={errors.diagnosis_date}>
                <Input
                  type="date"
                  value={form.diagnosis_date}
                  onChange={(e) => set('diagnosis_date', e.target.value)}
                  className={errors.diagnosis_date ? 'border-destructive' : ''}
                />
              </Field>
            )}
            {form.date_precision === 'approximate' && (
              <Field label="Fecha aproximada">
                <Input
                  value={form.date_approximate}
                  onChange={(e) => set('date_approximate', e.target.value)}
                  placeholder="ej. principios de 2018, alrededor de 2020"
                />
              </Field>
            )}
            {form.date_precision === 'year_only' && (
              <Field label="Año" error={errors.date_year}>
                <Input
                  type="number"
                  min={1900}
                  max={new Date().getFullYear()}
                  value={form.date_year}
                  onChange={(e) => set('date_year', e.target.value)}
                  placeholder="ej. 2015"
                  className={errors.date_year ? 'border-destructive' : ''}
                />
              </Field>
            )}
          </div>

          {/* ── Status ───────────────────────────────────────────────────── */}
          <Field label="Estado actual">
            <Select value={form.status} onValueChange={(v) => set('status', v as PathologicalEntry['status'])}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Activo</SelectItem>
                <SelectItem value="chronic">Crónico</SelectItem>
                <SelectItem value="under_follow_up">En seguimiento</SelectItem>
                <SelectItem value="resolved">Resuelto</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          {/* ── Type-specific fields ──────────────────────────────────────── */}
          {form.item_type === 'surgery' && (
            <ConditionalBlock title="Detalles de la cirugía">
              <Field label="Procedimiento realizado">
                <Input value={form.surgery_procedure} onChange={(e) => set('surgery_procedure', e.target.value)} placeholder="ej. Colecistectomía laparoscópica" />
              </Field>
              <Field label="Hospital / institución">
                <Input value={form.surgery_hospital} onChange={(e) => set('surgery_hospital', e.target.value)} placeholder="ej. Hospital Ángeles" />
              </Field>
              <Field label="Médico responsable (si se conoce)">
                <Input value={form.surgery_physician} onChange={(e) => set('surgery_physician', e.target.value)} placeholder="ej. Dr. Juan García" />
              </Field>
            </ConditionalBlock>
          )}

          {form.item_type === 'allergy' && (
            <ConditionalBlock title="Detalles de la alergia">
              <Field label="Agente causante">
                <Input value={form.allergy_causative_agent} onChange={(e) => set('allergy_causative_agent', e.target.value)} placeholder="ej. Penicilina, mariscos" />
              </Field>
              <Field label="Tipo de reacción">
                <Input value={form.allergy_reaction_type} onChange={(e) => set('allergy_reaction_type', e.target.value)} placeholder="ej. Urticaria, anafilaxia" />
              </Field>
              <Field label="Severidad">
                <Select value={form.allergy_severity} onValueChange={(v) => set('allergy_severity', v as 'mild' | 'moderate' | 'severe')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(['mild', 'moderate', 'severe'] as const).map((s) => (
                      <SelectItem key={s} value={s}>{SEVERITY_LABELS[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </ConditionalBlock>
          )}

          {form.item_type === 'chronic_degenerative' && (
            <ConditionalBlock title="Manejo crónico">
              <Field label="Tratamiento actual asociado">
                <Input value={form.chronic_treatment} onChange={(e) => set('chronic_treatment', e.target.value)} placeholder="ej. Metformina 850 mg c/12 h" />
              </Field>
              <Field label="Especialista que lleva el caso">
                <Input value={form.chronic_specialist} onChange={(e) => set('chronic_specialist', e.target.value)} placeholder="ej. Dr. López — Endocrinología" />
              </Field>
            </ConditionalBlock>
          )}

          {form.item_type === 'transfusion' && (
            <ConditionalBlock title="Detalles de la transfusión">
              <Field label="Motivo">
                <Input value={form.transfusion_reason} onChange={(e) => set('transfusion_reason', e.target.value)} placeholder="ej. Hemorragia postoperatoria" />
              </Field>
              <Field label="Tipo de hemoderivado">
                <Input value={form.transfusion_blood_product} onChange={(e) => set('transfusion_blood_product', e.target.value)} placeholder="ej. Concentrado eritrocitario, plasma" />
              </Field>
              <Field label="Reacciones presentadas (si las hubo)">
                <Input value={form.transfusion_reaction} onChange={(e) => set('transfusion_reaction', e.target.value)} placeholder="ej. Sin reacciones / Fiebre leve" />
              </Field>
            </ConditionalBlock>
          )}

          {form.item_type === 'hospitalization' && (
            <ConditionalBlock title="Detalles de la hospitalización">
              <Field label="Motivo">
                <Input value={form.hospitalization_reason} onChange={(e) => set('hospitalization_reason', e.target.value)} placeholder="ej. Neumonía bilateral" />
              </Field>
              <Field label="Duración">
                <Input value={form.hospitalization_duration} onChange={(e) => set('hospitalization_duration', e.target.value)} placeholder="ej. 5 días" />
              </Field>
              <Field label="Hospital / institución">
                <Input value={form.hospitalization_hospital} onChange={(e) => set('hospitalization_hospital', e.target.value)} placeholder="ej. IMSS HGZ 10" />
              </Field>
            </ConditionalBlock>
          )}

          {form.item_type === 'vaccination' && (
            <ConditionalBlock title="Detalles de la vacunación">
              <Field label="Nombre de la vacuna">
                <Input value={form.vaccination_name} onChange={(e) => set('vaccination_name', e.target.value)} placeholder="ej. Influenza trivalente" />
              </Field>
              <Field label="Dosis / refuerzo">
                <Input value={form.vaccination_dose} onChange={(e) => set('vaccination_dose', e.target.value)} placeholder="ej. 1ª dosis, Refuerzo anual" />
              </Field>
            </ConditionalBlock>
          )}

          {form.item_type === 'current_medication' && (
            <ConditionalBlock title="Detalles del medicamento">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Nombre">
                  <Input value={form.medication_name} onChange={(e) => set('medication_name', e.target.value)} placeholder="ej. Atorvastatina" />
                </Field>
                <Field label="Dosis">
                  <Input value={form.medication_dosage} onChange={(e) => set('medication_dosage', e.target.value)} placeholder="ej. 40 mg" />
                </Field>
                <Field label="Frecuencia">
                  <Input value={form.medication_frequency} onChange={(e) => set('medication_frequency', e.target.value)} placeholder="ej. Cada 24 horas" />
                </Field>
                <Field label="Fecha de inicio">
                  <Input type="date" value={form.medication_start_date} onChange={(e) => set('medication_start_date', e.target.value)} />
                </Field>
              </div>
            </ConditionalBlock>
          )}

          {/* ── Physician notes ───────────────────────────────────────────── */}
          <Field label="Notas del médico (opcional)">
            <Textarea
              value={form.physician_notes}
              onChange={(e) => set('physician_notes', e.target.value)}
              placeholder="Observaciones adicionales relevantes…"
              rows={3}
              className="resize-none"
            />
          </Field>

          {/* ── Related item link ─────────────────────────────────────────── */}
          {existingItems.length > 0 && (
            <Field label="Antecedente relacionado (opcional)">
              <Select
                value={form.related_item_idx !== '' ? String(form.related_item_idx) : '__none__'}
                onValueChange={(v) => set('related_item_idx', v === '__none__' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sin relación" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin relación</SelectItem>
                  {existingItems.map((item, i) => (
                    <SelectItem key={i} value={String(i)}>
                      {item.condition}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-ml-on-surface-variant/60">
                Enlace causa-efecto con otro antecedente ya registrado (ej. litiasis vesicular → colecistectomía).
              </p>
            </Field>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div className="flex justify-end gap-3 border-t border-ml-outline-variant/15 pt-4">
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            className="bg-ml-primary text-white hover:bg-ml-primary-container"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Guardar antecedente
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function Field({
  label,
  error,
  children,
}: {
  label: string
  error?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <Label className="text-xs font-medium">{label}</Label>
      <div className="mt-1">{children}</div>
      {error && <p className="mt-1 text-[11px] text-destructive">{error}</p>}
    </div>
  )
}

function ConditionalBlock({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-lg border border-ml-outline-variant/20 bg-ml-surface-container-lowest/40 p-4 space-y-3">
      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-ml-on-surface-variant/70">
        {title}
      </p>
      {children}
    </div>
  )
}
